import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';

import { endpoints } from '@/config/apiEndpoints';
import { collections } from '@/config/databasePaths';
import { FIRESTORE_ENABLED } from '@/config/env';
import { useFirestoreDocument } from '@/hooks/useLiveData';
import type { TranslationKey } from '@/i18n';
import { apiClient, isForbidden, refusalKey } from '@/lib/apiClient';
import { currentUser, signInWithGoogle } from '@/lib/firebase';
import type { Organization } from '@/features/organization/types';
import { requireParsed } from '@/lib/readers';
import {
  endFirebaseSession,
  type Membership,
  type Session,
  signIn,
  signOut,
  updateMemberships,
} from './session';
import { parseMemberships, parseSessionResponse, type SessionResponse } from './types';
import { useSession } from './useSession';

export class NoOrganizationError extends Error {
  constructor() {
    super('The session carries no organization to open.');
    this.name = 'NoOrganizationError';
  }
}

const REFUSALS: Record<number, TranslationKey> = {
  403: 'login.notWhitelisted',
};

/** Returns the message key of a failed sign-in. */
export function signInFailureKey(error: unknown): TranslationKey {
  return error instanceof NoOrganizationError ? 'login.noOrganization' : refusalKey(error, REFUSALS, 'login.failed');
}

/** Opens the Google popup and returns a new ID token. It never returns a stored token. */
async function requestGoogleIdToken(): Promise<string> {
  const user = await signInWithGoogle();
  return user.getIdToken(true);
}

/** Returns a force-refreshed ID token, or the fallback with Firestore disabled or nobody signed in. */
async function refreshIdToken(fallback: string): Promise<string> {
  if (!FIRESTORE_ENABLED) return fallback;
  const user = await currentUser();
  return user ? user.getIdToken(true) : fallback;
}

function toSession(payload: SessionResponse, token: string): Session {
  return {
    token,
    name: payload.name,
    email: payload.email,
    organizationId: payload.organizationId,
    memberships: payload.memberships,
  };
}

/** Sends one session request and returns its parsed response. */
async function requestSession(
  path: string,
  body: Record<string, unknown>,
  headers?: Record<string, string>,
): Promise<SessionResponse> {
  return requireParsed(parseSessionResponse(await apiClient.post(path, body, { headers })), 'session');
}

export interface SignInResult {
  session: Session;
  organizations: Organization[];
}

export interface GoogleSignInOptions {
  onUndecided: (organizations: Organization[], token: string) => void;
}

/** Returns the mutation that verifies the Google sign-in. It stores the session when one
 *  membership comes back, calls `onUndecided` when several do, and ends a refused Google session. */
export function useGoogleSignIn({ onUndecided }: GoogleSignInOptions) {
  return useMutation<SignInResult>({
    mutationFn: async () => {
      const idToken = FIRESTORE_ENABLED ? await requestGoogleIdToken() : null;
      const payload = await requestSession(endpoints.auth.session(), { id_token: idToken });
      const token = payload.organizationId ? await refreshIdToken(payload.token) : payload.token;
      const session = toSession(payload, token);
      if (!session.organizationId && payload.organizations.length < 2) {
        throw new NoOrganizationError();
      }
      return { session, organizations: payload.organizations };
    },
    meta: { toastsError: false },
    onError: async (error) => {
      if (isForbidden(error)) await endFirebaseSession();
    },
    onSuccess: ({ session, organizations }) => {
      if (session.organizationId) {
        signIn(session);
        return;
      }
      onUndecided(organizations, session.token);
    },
  });
}

export interface OrganizationSelection {
  organizationId: string;
  token: string;
}

/** Returns the mutation that sends the selected organization with the sign-in token,
 *  and stores the session with a new token. */
export function useSelectOrganization() {
  return useMutation<Session, Error, OrganizationSelection>({
    mutationFn: async ({ organizationId, token }) => {
      const payload = await requestSession(
        endpoints.auth.selectOrganization(),
        { organization_id: organizationId },
        { Authorization: `Bearer ${token}` },
      );
      return toSession(payload, await refreshIdToken(payload.token));
    },
    meta: { toastsError: false },
    onSuccess: signIn,
  });
}

/** Keeps the role and permissions of the signed-in user current. It ends the session
 *  when the membership becomes inactive. */
export function useLiveMembership(): void {
  const session = useSession();
  const email = session?.email ?? null;
  const organizationId = session?.organizationId ?? null;

  const { data } = useFirestoreDocument<Membership[]>({
    path: collections.user(email ?? '_'),
    parse: parseMemberships,
    enabled: Boolean(email),
  });

  useEffect(() => {
    if (!data || !organizationId) return;
    if (!data.some((membership) => membership.organizationId === organizationId)) {
      signOut('revoked');
      return;
    }
    updateMemberships(data);
  }, [data, organizationId]);
}
